import { useState, useMemo, useEffect } from "react";
import { computePanelMetrics, fmt } from "../lib/calc";
import { API } from "../lib/api";
import { X, ExternalLink } from "lucide-react";
import { DrawingViewer } from "./DrawingViewer";

const SHAPES = ["rectangle", "l-shape", "t-shape", "curved", "custom"];
const COLORS = ["#2560C9", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#C5CBD4", "#3B75DF", "#14B8A6"];

export function PanelForm({ onClose, onCreate, onUpdate, initial }) {
  const isEdit = !!initial;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [form, setForm] = useState(() =>
    isEdit
      ? {
          name: initial.name || "",
          jobNumber: initial.jobNumber || "",
          width: initial.width ?? "",
          length: initial.length ?? "",
          thickness: initial.thickness ?? "",
          strength28: initial.strength28 ?? "40",
          shape: initial.shape || "rectangle",
          areaOverride: initial.area ?? "",
          volumeOverride: initial.volume ?? "",
          massOverride: initial.mass ?? "",
          color: initial.color || "#2560C9",
        }
      : {
          name: "",
          jobNumber: "",
          width: "",
          length: "",
          thickness: "",
          strength28: "40",
          shape: "rectangle",
          areaOverride: "",
          volumeOverride: "",
          massOverride: "",
          color: "#2560C9",
        }
  );

  const metrics = useMemo(() => computePanelMetrics(form), [form]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isCustomShape = form.shape !== "rectangle";
  const showOverrides = isEdit || isCustomShape;
  const hasDrawing = isEdit && !!initial?.drawingId;

  const autoFit = () => {
    const w0 = parseFloat(form.width) || 0;
    const l0 = parseFloat(form.length) || 0;
    const area = parseFloat(form.areaOverride) || (w0 && l0 ? w0 * l0 : 0);
    if (!area) return;
    let w = w0, l = l0;
    if (w0 > 0 && l0 > 0) {
      const k = Math.sqrt(area / (w0 * l0));
      w = w0 * k;
      l = l0 * k;
    } else if (w0 > 0) {
      l = area / w0;
    } else if (l0 > 0) {
      w = area / l0;
    } else {
      w = Math.sqrt(area);
      l = Math.sqrt(area);
    }
    set("width", +w.toFixed(2));
    set("length", +l.toFixed(2));
  };

  const submit = (e) => {
    e.preventDefault();
    const base = {
      name: form.name || `Panel ${Math.floor(Math.random() * 9000 + 1000)}`,
      jobNumber: (form.jobNumber || "").trim(),
      width: parseFloat(form.width) || 0,
      length: parseFloat(form.length) || 0,
      thickness: parseFloat(form.thickness) || 0,
      strength28: parseFloat(form.strength28) || 0,
      shape: form.shape,
      color: form.color,
      ...metrics,
    };
    if (isEdit) onUpdate({ ...initial, ...base });
    else onCreate({ id: `panel-${Date.now()}-${Math.floor(Math.random() * 999)}`, ...base });
  };

  const field =
    "w-full mt-1 bg-[#0A0A0A] border border-[#2E3033] px-3 py-2 text-white placeholder-[#6E7787] focus:border-[#2560C9] focus:outline-none transition-all font-mono text-sm";
  const lbl = "text-[10px] font-mono uppercase tracking-[0.15em] text-[#6E7787]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        data-testid="panel-form-modal"
        className={`w-full ${hasDrawing ? "max-w-6xl" : "max-w-lg"} bg-[#16181A] border border-[#2E3033] shadow-[0_4px_40px_rgba(0,0,0,0.7)] max-h-[92vh] overflow-y-auto`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2E3033] sticky top-0 bg-[#16181A] z-10">
          <h3 className="font-heading font-bold text-white tracking-tight">{isEdit ? "EDIT PANEL" : "ADD PANEL"}</h3>
          <button data-testid="panel-form-close" onClick={onClose} className="text-[#6E7787] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={hasDrawing ? "md:grid md:grid-cols-[2.2fr_1fr] md:divide-x md:divide-[#2E3033]" : ""}>
          {hasDrawing && (
            <div className="p-5 border-b md:border-b-0 border-[#2E3033]">
              <div className="flex items-center justify-between mb-2">
                <span className={lbl}>Shop Drawing</span>
                <a
                  data-testid="panel-form-drawing-open"
                  href={`${API}/panels/drawing/${initial.drawingId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-[#A0AABF] hover:text-white text-[11px] font-mono"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
              </div>
              <DrawingViewer
                src={`${API}/panels/drawing/${initial.drawingId}`}
                alt={`Drawing ${initial.name}`}
                maxHeight="82vh"
                testid="panel-form-drawing"
              />
            </div>
          )}
          <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Job Number</label>
              <input data-testid="panel-job-input" value={form.jobNumber} onChange={(e) => set("jobNumber", e.target.value)} className={field} placeholder="P159" />
            </div>
            <div>
              <label className={lbl}>Panel Name / ID</label>
              <input data-testid="panel-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={field} placeholder="WALL-P01" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={lbl}>Width (m)</label>
              <input data-testid="panel-width-input" type="number" step="0.01" required value={form.width} onChange={(e) => set("width", e.target.value)} className={field} placeholder="2.4" />
            </div>
            <div>
              <label className={lbl}>Length (m)</label>
              <input data-testid="panel-length-input" type="number" step="0.01" required value={form.length} onChange={(e) => set("length", e.target.value)} className={field} placeholder="6.0" />
            </div>
            <div>
              <label className={lbl}>Thick. (m)</label>
              <input data-testid="panel-thickness-input" type="number" step="0.001" required value={form.thickness} onChange={(e) => set("thickness", e.target.value)} className={field} placeholder="0.15" />
            </div>
          </div>

          <button
            type="button"
            data-testid="panel-autofit-button"
            onClick={autoFit}
            className="w-full border border-[#2560C9]/50 text-[#3B75DF] hover:bg-[#2560C9]/10 text-[11px] font-mono uppercase tracking-wider py-2 transition-colors"
            title="Set width × length to match the panel area (useful for imported custom shapes)"
          >
            ⤢ Auto-fit footprint from area
          </button>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Shape</label>
              <select data-testid="panel-shape-select" value={form.shape} onChange={(e) => set("shape", e.target.value)} className={field}>
                {SHAPES.map((s) => (
                  <option key={s} value={s} className="bg-[#0A0A0A]">
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={lbl}>28-Day Strength (MPa)</label>
              <input data-testid="panel-strength-input" type="number" step="1" value={form.strength28} onChange={(e) => set("strength28", e.target.value)} className={field} placeholder="40" />
            </div>
          </div>

          {showOverrides && (
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={lbl}>Area (m²)</label>
                <input data-testid="panel-area-override-input" type="number" step="0.01" value={form.areaOverride} onChange={(e) => set("areaOverride", e.target.value)} className={field} placeholder="W × L" />
              </div>
              <div>
                <label className={lbl}>Volume (m³)</label>
                <input data-testid="panel-volume-override-input" type="number" step="0.001" value={form.volumeOverride} onChange={(e) => set("volumeOverride", e.target.value)} className={field} placeholder="auto" />
              </div>
              <div>
                <label className={lbl}>Mass (kg)</label>
                <input data-testid="panel-mass-override-input" type="number" step="1" value={form.massOverride} onChange={(e) => set("massOverride", e.target.value)} className={field} placeholder="auto" />
              </div>
            </div>
          )}

          <div>
            <label className={lbl}>Color</label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`panel-color-${c}`}
                  onClick={() => set("color", c)}
                  className={`w-7 h-7 border-2 transition-all ${form.color === c ? "border-white scale-110" : "border-transparent"}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 bg-[#0A0A0A] border border-[#2E3033] p-3">
            <Metric label="Area" value={`${fmt(metrics.area)} m²`} />
            <Metric label="Volume" value={`${fmt(metrics.volume, 3)} m³`} />
            <Metric label="Mass" value={`${fmt(metrics.mass / 1000, 2)} t`} />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 border border-[#2E3033] hover:bg-[#22252A] text-white py-2.5 transition-colors">
              Cancel
            </button>
            <button data-testid="panel-form-submit" type="submit" className="flex-1 bg-[#2560C9] hover:bg-[#3B75DF] text-white font-medium py-2.5 transition-colors">
              {isEdit ? "Save Changes" : "Add to Queue"}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#6E7787]">{label}</p>
      <p className="text-sm font-mono text-white mt-0.5">{value}</p>
    </div>
  );
}
